import { getNostrPool } from '../lib/nostrPool'
import { RELAYS } from '../config/constants'
import { finalizeEvent } from 'nostr-tools'

export type ReportReason = 'spam' | 'illegal' | 'nudity' | 'profanity' | 'impersonation' | 'other'

export const reportService = {
    /**
     * Send a report (Kind 1984)
     * @param targetId The ID of the event or pubkey being reported
     * @param targetType 'event' or 'user' (pubkey)
     * @param reason The standardized reason
     * @param comment Optional comment
     * @param signer Sk (Uint8Array) or explicit signer function
     */
    sendReport: async (
        targetId: string,
        targetType: 'event' | 'user',
        reason: ReportReason,
        comment: string,
        sk: Uint8Array | null
    ) => {
        console.log('[ReportService] sendReport started', { targetId, targetType, reason, comment })
        const pool = getNostrPool()

        const tags = [
            ['p', targetType === 'user' ? targetId : ''], // If reporting user, p tag is target
            ['e', targetType === 'event' ? targetId : ''], // If reporting event, e tag is target
            ['report', reason],
            ['t', 'mavililer']
        ].filter(t => t[1]) // Remove empty tags

        const event = {
            kind: 1984,
            created_at: Math.floor(Date.now() / 1000),
            tags: tags,
            content: comment,
        }

        if (sk) {
            const signed = finalizeEvent(event as any, sk)
            console.log('[ReportService] Signed with SK:', signed)
            await Promise.any(pool.publish(RELAYS, signed))
            console.log('[ReportService] Published to relays')
            return signed
        }

        if (window.nostr) {
            // @ts-ignore
            const signed = await window.nostr.signEvent({ ...event, pubkey: await window.nostr.getPublicKey() })
            console.log('[ReportService] Signed (Extension):', signed)
            await Promise.any(pool.publish(RELAYS, signed))
            console.log('[ReportService] Published to relays')
            return signed
        }

        throw new Error('No signer available')
    },

    /**
     * Fetch reports for admin dashboard
     */
    fetchReports: async () => {
        console.log('[ReportService] fetchReports started')
        const pool = getNostrPool()
        const events: any[] = []

        // Debug Relays - explicitly reliable ones
        const INFO_RELAYS = [
            'wss://relay.damus.io',
            'wss://nos.lol',
            'wss://relay.primal.net'
        ]

        return new Promise<any[]>((resolve) => {
            const filter = {
                kinds: [1984, 1], // Fetching Kind 1 too to verify connectivity!
                // '#t': ['mavililer'],
                limit: 10
            }
            console.log('[ReportService] Subscribing with filter:', filter, 'to relays:', INFO_RELAYS)

            const sub = pool.subscribeMany(INFO_RELAYS, [filter] as any, {
                onevent(event) {
                    console.log('[ReportService] Event Received:', event.kind, event.id)
                    // Only keep 1984s for the result, but log all
                    if (event.kind === 1984) {
                        if (!events.some(e => e.id === event.id)) {
                            events.push(event)
                        }
                    }
                },
                oneose() {
                    console.log('[ReportService] EOSE received')
                    sub.close()
                    resolve(events.sort((a, b) => b.created_at - a.created_at))
                },
            })

            // Timeout fallback
            setTimeout(() => {
                console.log('[ReportService] Timeout reached. Returning events:', events.length)
                sub.close()
                resolve(events.sort((a, b) => b.created_at - a.created_at))
            }, 3000)
        })
    },

    /**
     * Delete reported content (Send Kind 5)
     */
    deleteReportedContent: async (eventId: string, reason: string = 'admin deletion') => {
        if (window.nostr) {
            const event = {
                kind: 5,
                created_at: Math.floor(Date.now() / 1000),
                tags: [['e', eventId]],
                content: reason,
                pubkey: '' // window.nostr will fill
            }
            const signed = await window.nostr.signEvent(event)
            const pool = getNostrPool()
            await Promise.any(pool.publish(RELAYS, signed))
            return signed
        }
        throw new Error('No signer available')
    }
}
